import React, { useState, useRef, useEffect } from 'react';
import {
  Typography,
  Button,
  Box,
  CircularProgress,
  TextField,
  MenuItem,
  Select,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import MergeIcon from '@mui/icons-material/CallMerge';
import SplitscreenIcon from '@mui/icons-material/Splitscreen';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReorderIcon from '@mui/icons-material/Reorder';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import CompressIcon from '@mui/icons-material/Compress';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpInfoIcon from '../components/HelpInfoIcon';
import InterfaceHeader from '../components/InterfaceHeader';
import { useActivityLog } from '../context/ActivityLogContext';
import API_BASE_URL from '../config/api';
import FavoriteButton from '../components/FavoriteButton';
import { TOOL_COLORS, getCardBackground } from '../constants/colors';

const API_BASE = API_BASE_URL;

const PDFManipulation = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    addActivityProcess,
    updateActivityProcess,
    addLog,
    completeActivityProcess,
  } = useActivityLog();

  // ---------- state ----------
  const [files, setFiles] = useState([]);
  const [insertFile, setInsertFile] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [splitPageCount, setSplitPageCount] = useState('1');
  const [deletePageRange, setDeletePageRange] = useState('2-3,5,7-9');
  const [insertAt, setInsertAt] = useState('2');
  const [reorderPages, setReorderPages] = useState('2,1,3,4');
  const [watermarkText, setWatermarkText] = useState('SAFAVIEH');
  const [exportFormat, setExportFormat] = useState('DOCX');
  const [selectedPDF, setSelectedPDF] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isInsertDragging, setIsInsertDragging] = useState(false);

  // ---------- refs ----------
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);
  const fileInputRef = useRef(null);
  const insertFileInputRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);

  // ---------- constants ----------
  const isDarkMode = theme.palette.mode === 'dark';
  const primaryColor = TOOL_COLORS.PDF.primary;
  const primaryHover = isDarkMode ? '#FF1744' : '#B91C3C';

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      color: theme.palette.text.primary,
      '& fieldset': {
        borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23),
      },
      '&:hover fieldset': {
        borderColor: primaryColor,
      },
      '&.Mui-focused fieldset': {
        borderColor: primaryColor,
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
    },
  };

  const selectStyles = {
    color: theme.palette.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: isDarkMode ? alpha('#fff', 0.23) : alpha('#000', 0.23),
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: primaryColor,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: primaryColor,
    },
    '& .MuiSvgIcon-root': {
      color: theme.palette.text.secondary,
    },
  };

  const actionBtnSx = {
    flex: 1,
    py: 1.2,
    backgroundColor: primaryColor,
    color: 'white',
    textTransform: 'none',
    fontWeight: 500,
    '&:hover': { backgroundColor: primaryHover },
    '&:disabled': {
      backgroundColor: isDarkMode ? alpha('#fff', 0.12) : theme.palette.grey[300],
      color: isDarkMode ? alpha('#fff', 0.3) : theme.palette.grey[500],
    },
  };

  // ---------- PDF Preview ----------
  useEffect(() => {
    if (selectedPDF) {
      const url = URL.createObjectURL(selectedPDF);
      setPdfBlobUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPdfBlobUrl(null);
    }
  }, [selectedPDF]);

  // ---------- file handlers ----------
  const handleFileChange = (e) => {
    const allFiles = Array.from(e.target.files);
    const pdfs = allFiles.filter((f) => f.type === 'application/pdf');
    if (allFiles.length > pdfs.length) {
      setError(`Warning: ${allFiles.length - pdfs.length} non-PDF file(s) were ignored. Only PDF files are supported.`);
    } else {
      setError(null);
    }
    setFiles(pdfs);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleInsertFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setInsertFile(file);
      setError(null);
    } else {
      setInsertFile(null);
    }
    if (insertFileInputRef.current) insertFileInputRef.current.value = '';
  };

  const handleDrop = (e, isInsert = false) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setIsInsertDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const pdfs = droppedFiles.filter((f) => f.type === 'application/pdf');
    if (droppedFiles.length > pdfs.length) {
      setError(`Warning: ${droppedFiles.length - pdfs.length} non-PDF file(s) were ignored.`);
    } else {
      setError(null);
    }
    if (isInsert) {
      if (pdfs.length > 0) setInsertFile(pdfs[0]);
    } else {
      setFiles(pdfs);
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); };
  const handleDragEnterZone = (e, isInsert = false) => { e.preventDefault(); e.stopPropagation(); isInsert ? setIsInsertDragging(true) : setIsDragging(true); };
  const handleDragLeaveZone = (e, isInsert = false) => { e.preventDefault(); e.stopPropagation(); isInsert ? setIsInsertDragging(false) : setIsDragging(false); };

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    if (selectedPDF && files[index]?.name === selectedPDF.name) {
      setSelectedPDF(newFiles[0] || null);
    }
  };

  const removeInsertFile = () => setInsertFile(null);
  const handleSelectPDF = (file) => { selectedPDF?.name === file.name ? setSelectedPDF(null) : setSelectedPDF(file); };

  const handleDragStart = (idx) => (dragItem.current = idx);
  const handleDragEnter = (idx) => (dragOverItem.current = idx);
  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null) {
      const newFiles = [...files];
      const [moved] = newFiles.splice(dragItem.current, 1);
      newFiles.splice(dragOverItem.current, 0, moved);
      setFiles(newFiles);
      setSelectedPDF(newFiles[0] || null);
    }
    dragItem.current = dragOverItem.current = null;
  };

  // ---------- generic API helper ----------
  const handleApiCall = async (endpoint, formData, fileNamePrefix, isMultipleFiles = false) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('jwtToken');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const activityProcessId = `pdf-manipulation-${Date.now()}`;
    const actionName = fileNamePrefix.replace(/PDF$/, '').replace(/([A-Z])/g, ' $1').trim();
    addActivityProcess(activityProcessId, `PDF ${actionName}`, 'PDF Manipulation');
    addLog(activityProcessId, `Starting ${actionName}...`);
    addLog(activityProcessId, `Files: ${files.map(f => f.name).join(', ')}`);

    try {
      addLog(activityProcessId, `⏳ Processing PDF file(s)...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 30 });
      const res = await fetch(`${API_BASE}/api/PDFManipulation/${endpoint}`, { method: 'POST', headers, body: formData });
      addLog(activityProcessId, `⏳ Generating output...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 60 });

      if (!res.ok) {
        let errorMessage;
        try { errorMessage = await res.text(); } catch (e) { errorMessage = `HTTP ${res.status}: ${res.statusText}`; }
        throw new Error(errorMessage || `Failed to ${endpoint}. Status: ${res.status}`);
      }

      const disp = res.headers.get('Content-Disposition');
      let fileName = `${fileNamePrefix} ${new Date().toISOString().replace(/[-:T.]/g, '').slice(0, 14)}${isMultipleFiles ? '.zip' : ''}`;
      if (disp) { const match = disp.match(/filename="(.+)"/); if (match) fileName = match[1]; }

      addLog(activityProcessId, `⏳ Preparing download...`);
      updateActivityProcess(activityProcessId, { progressPercentage: 80 });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = fileName; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);

      if (!isMultipleFiles && res.headers.get('Content-Type') === 'application/pdf') {
        setSelectedPDF(new File([blob], fileName, { type: 'application/pdf' }));
      }

      updateActivityProcess(activityProcessId, { progressPercentage: 100 });
      addLog(activityProcessId, `✓ ${actionName} completed successfully`);
      addLog(activityProcessId, `✓ File downloaded: ${fileName}`);
      completeActivityProcess(activityProcessId, true);
    } catch (err) {
      console.error(err);
      addLog(activityProcessId, `✗ Error: ${err.message}`);
      completeActivityProcess(activityProcessId, false);
      setError(err.message || 'Unexpected error - see console for details.');
    } finally {
      setLoading(false);
    }
  };

  // ---------- action handlers ----------
  const handleMerge = async () => { if (files.length < 2) return setError('Select at least two PDFs to merge.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); await handleApiCall('merge', fd, 'MergedPDF'); };
  const handleSplit = async () => { if (!files.length) return setError('Select at least one PDF to split.'); if (!/^[0-9]+$/.test(splitPageCount) || +splitPageCount <= 0) return setError('Pages per chunk must be a positive integer.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); fd.append('pagesPerChunk', splitPageCount); await handleApiCall('split', fd, 'SplitPDFs', true); };
  const handleDeletePages = async () => { if (!files.length) return setError('Select at least one PDF.'); if (!deletePageRange.match(/^(\d+|\d+-\d+)(,(\d+|\d+-\d+))*$/)) return setError('Invalid range (e.g., 1,3-4).'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); fd.append('pageRanges', deletePageRange); await handleApiCall('deletepages', fd, 'DeletedPagesPDF', true); };
  const handleExport = async () => { if (!files.length) return setError('Select a PDF to export.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); fd.append('format', exportFormat); await handleApiCall('export', fd, 'ExportedFile', files.length > 1); };
  const handleInsertPages = async () => { if (!files.length) return setError('Select at least one base PDF.'); if (!insertFile) return setError('Choose a PDF to insert.'); if (!/^[0-9]+$/.test(insertAt) || +insertAt <= 0) return setError('Insert page must be positive integer.'); const fd = new FormData(); files.forEach((f) => fd.append('baseFiles', f)); fd.append('insertFile', insertFile); fd.append('insertAtPage', insertAt); await handleApiCall('insertpages', fd, 'InsertedPagesPDF', true); };
  const handleReorderPages = async () => { if (!files.length) return setError('Select a PDF.'); if (!reorderPages.match(/^\d+(,\d+)*$/)) return setError('Invalid order list.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); fd.append('pageOrder', reorderPages); await handleApiCall('reorderpages', fd, 'ReorderedPagesPDF', files.length > 1); };
  const handleWatermark = async () => { if (!files.length) return setError('Select a PDF.'); if (!watermarkText.trim()) return setError('Enter watermark text.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); fd.append('watermarkText', watermarkText); await handleApiCall('watermark', fd, 'WatermarkedPDF', files.length > 1); };
  const handleCompress = async () => { if (!files.length) return setError('Select a PDF.'); const fd = new FormData(); files.forEach((f) => fd.append('files', f)); await handleApiCall('compress', fd, 'CompressedPDF', files.length > 1); };

  const handleClear = () => {
    setFiles([]); setInsertFile(null); setSelectedPDF(null); setSplitPageCount('1');
    setDeletePageRange('2-3,5,7-9'); setInsertAt('2'); setReorderPages('2,1,3,4');
    setWatermarkText('SAFAVIEH'); setExportFormat('DOCX'); setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (insertFileInputRef.current) insertFileInputRef.current.value = '';
  };

  // ---------- render ----------
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: theme.palette.background.default }}>
      <Box sx={{ p: 3, width: '100%', maxWidth: 1200, mx: 'auto', position: 'relative' }}>
        <FavoriteButton interfaceName="PDF Manipulation" category="pdf" />
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: theme.palette.text.primary,
            textAlign: 'center',
            fontWeight: 600,
            letterSpacing: '0.5px',
            borderBottom: `2px solid ${theme.palette.divider}`,
            pb: 2,
            mb: 4
          }}
        >
          PDF MANIPULATION
        </Typography>

        <Grid container spacing={3}>
          {/* Main Section */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Upload & Process
              </Typography>

              {/* Drop Zone */}
              <input type="file" accept="application/pdf" multiple ref={fileInputRef} onChange={handleFileChange} style={{ display: 'none' }} />
              <Box
                onClick={() => fileInputRef.current?.click()}
                onDrop={(e) => handleDrop(e, false)}
                onDragOver={handleDragOver}
                onDragEnter={(e) => handleDragEnterZone(e, false)}
                onDragLeave={(e) => handleDragLeaveZone(e, false)}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: `2px dashed ${isDragging ? primaryHover : alpha(primaryColor, 0.4)}`,
                  backgroundColor: isDragging ? alpha(primaryColor, 0.1) : files.length > 0 ? alpha('#4caf50', isDarkMode ? 0.1 : 0.05) : isDarkMode ? alpha(theme.palette.background.paper, 0.3) : '#fafafa',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  mb: 3,
                  '&:hover': { borderColor: primaryHover, backgroundColor: alpha(primaryColor, 0.06) },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 40, color: alpha(primaryColor, 0.6), mb: 0.5 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {isDragging ? 'Drop PDF files here' : files.length > 0 ? `${files.length} PDF file(s) selected` : 'Click or drag PDF files here'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Supports multiple PDF files
                </Typography>
              </Box>

              {/* Actions */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                Actions
              </Typography>

              {/* Row 1: Combine & Compress */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Button disabled={loading || files.length < 2} variant="contained" onClick={handleMerge} startIcon={<MergeIcon />} sx={actionBtnSx}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Combine'}
                </Button>
                <Button disabled={loading || !files.length} variant="contained" onClick={handleCompress} startIcon={<CompressIcon />} sx={actionBtnSx}>
                  {loading ? <CircularProgress size={20} color="inherit" /> : 'Compress'}
                </Button>
              </Box>

              {/* Row 2: Split */}
              <Card sx={{ mb: 1.5, bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleSplit} startIcon={<SplitscreenIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Split'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Pages per chunk:</Typography>
                    <TextField value={splitPageCount} onChange={(e) => setSplitPageCount(e.target.value)} size="small" sx={{ width: 80, ...textFieldStyles }} />
                  </Box>
                </CardContent>
              </Card>

              {/* Row 3: Delete Pages */}
              <Card sx={{ mb: 1.5, bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleDeletePages} startIcon={<DeleteIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete Pages'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Ranges:</Typography>
                    <TextField value={deletePageRange} onChange={(e) => setDeletePageRange(e.target.value)} size="small" sx={{ flex: 1, ...textFieldStyles }} placeholder="e.g. 2-3,5,7-9" />
                  </Box>
                </CardContent>
              </Card>

              {/* Row 4: Export */}
              <Card sx={{ mb: 1.5, bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleExport} startIcon={<FileDownloadIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Export'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Format:</Typography>
                    <Select value={exportFormat} onChange={(e) => setExportFormat(e.target.value)} size="small" sx={{ width: 100, ...selectStyles }}>
                      <MenuItem value="DOCX">DOCX</MenuItem>
                      <MenuItem value="XLSX">XLSX</MenuItem>
                      <MenuItem value="PPTX">PPTX</MenuItem>
                    </Select>
                  </Box>
                </CardContent>
              </Card>

              {/* Row 5: Insert Pages */}
              <Card sx={{ mb: 1.5, bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleInsertPages} startIcon={<InsertDriveFileIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Insert Pages'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>At page:</Typography>
                    <TextField value={insertAt} onChange={(e) => setInsertAt(e.target.value)} size="small" sx={{ width: 70, ...textFieldStyles }} />
                    <input type="file" accept="application/pdf" ref={insertFileInputRef} onChange={handleInsertFileChange} style={{ display: 'none' }} />
                    <Box
                      onClick={() => insertFileInputRef.current?.click()}
                      onDrop={(e) => handleDrop(e, true)}
                      onDragOver={handleDragOver}
                      onDragEnter={(e) => handleDragEnterZone(e, true)}
                      onDragLeave={(e) => handleDragLeaveZone(e, true)}
                      sx={{
                        px: 1.5, py: 0.5, borderRadius: 1,
                        border: `1px dashed ${isInsertDragging ? primaryHover : alpha(primaryColor, 0.4)}`,
                        backgroundColor: insertFile ? alpha('#4caf50', isDarkMode ? 0.1 : 0.05) : 'transparent',
                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: primaryHover, backgroundColor: alpha(primaryColor, 0.06) },
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                        {insertFile ? insertFile.name : 'Select insert file'}
                      </Typography>
                      {insertFile && (
                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeInsertFile(); }} sx={{ p: 0.2, '&:hover': { color: theme.palette.error.main } }}>
                          <CloseIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Row 6: Reorder */}
              <Card sx={{ mb: 1.5, bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleReorderPages} startIcon={<ReorderIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Reorder'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Page order:</Typography>
                    <TextField value={reorderPages} onChange={(e) => setReorderPages(e.target.value)} size="small" sx={{ flex: 1, ...textFieldStyles }} placeholder="e.g. 2,1,3,4" />
                  </Box>
                </CardContent>
              </Card>

              {/* Row 7: Watermark */}
              <Card sx={{ bgcolor: getCardBackground(theme) }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Button disabled={loading || !files.length} variant="contained" onClick={handleWatermark} startIcon={<BrandingWatermarkIcon />} sx={{ ...actionBtnSx, flex: 'none', width: 160 }}>
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Watermark'}
                    </Button>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>Text:</Typography>
                    <TextField value={watermarkText} onChange={(e) => setWatermarkText(e.target.value)} size="small" sx={{ flex: 1, ...textFieldStyles }} />
                  </Box>
                </CardContent>
              </Card>
            </Paper>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: 'hidden' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Files & Preview
              </Typography>

              {/* Error */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* File list */}
              {files.length > 0 ? (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Drag to reorder, click to preview ({files.length} file{files.length > 1 ? 's' : ''})
                  </Typography>
                  <List sx={{ p: 0, overflow: 'hidden' }}>
                    {files.map((f, i) => (
                      <ListItem
                        key={i}
                        draggable
                        onDragStart={() => handleDragStart(i)}
                        onDragEnter={() => handleDragEnter(i)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                        onClick={() => handleSelectPDF(f)}
                        sx={{
                          bgcolor: selectedPDF?.name === f.name ? alpha(primaryColor, isDarkMode ? 0.15 : 0.08) : isDarkMode ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
                          border: `1px solid ${selectedPDF?.name === f.name ? alpha(primaryColor, 0.4) : isDarkMode ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                          borderRadius: 1,
                          mb: 0.5,
                          px: 1,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: alpha(primaryColor, isDarkMode ? 0.12 : 0.06),
                            borderColor: alpha(primaryColor, 0.3),
                          },
                        }}
                      >
                        <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5, cursor: 'grab' }} />
                        <ListItemText
                          primary={f.name}
                          primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                        />
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                          sx={{ p: 0.3, color: 'text.secondary', '&:hover': { color: theme.palette.error.main } }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                </>
              ) : (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <DescriptionIcon sx={{ fontSize: 40, color: alpha(primaryColor, 0.3), mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No files selected yet
                  </Typography>
                </Box>
              )}

              {/* Insertion file */}
              {insertFile && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Insertion File:
                  </Typography>
                  <ListItem
                    onClick={() => handleSelectPDF(insertFile)}
                    sx={{
                      bgcolor: isDarkMode ? alpha(theme.palette.background.paper, 0.8) : 'background.paper',
                      border: `1px solid ${alpha('#4caf50', 0.3)}`,
                      borderRadius: 1,
                      px: 1,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: alpha('#4caf50', isDarkMode ? 0.1 : 0.05) },
                    }}
                  >
                    <ListItemText
                      primary={insertFile.name}
                      secondary="(Insert file)"
                      primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                    />
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); removeInsertFile(); }} sx={{ p: 0.3, '&:hover': { color: theme.palette.error.main } }}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </ListItem>
                </Box>
              )}

              {/* Clear button */}
              {(files.length > 0 || insertFile) && (
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  disabled={loading}
                  fullWidth
                  sx={{
                    mt: 2,
                    color: primaryColor,
                    borderColor: alpha(primaryColor, 0.5),
                    textTransform: 'none',
                    '&:hover': { borderColor: primaryColor, backgroundColor: alpha(primaryColor, 0.06) },
                  }}
                >
                  Clear All
                </Button>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Instructions */}
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Available Operations
              </Typography>
              <Typography variant="body2" color="text.secondary" component="div">
                <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
                  <li><strong>Combine</strong> — Merge multiple PDFs into one</li>
                  <li><strong>Split</strong> — Split into chunks by page count</li>
                  <li><strong>Delete</strong> — Remove specific pages</li>
                  <li><strong>Export</strong> — Convert to DOCX, XLSX, or PPTX</li>
                  <li><strong>Insert</strong> — Insert pages from another PDF</li>
                  <li><strong>Reorder</strong> — Rearrange page order</li>
                  <li><strong>Watermark</strong> — Add text watermark</li>
                  <li><strong>Compress</strong> — Reduce file size</li>
                </ul>
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* PDF Preview */}
        {selectedPDF && (
          <Paper elevation={0} sx={{ mt: 3, p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Preview: {selectedPDF.name}
            </Typography>
            {pdfBlobUrl ? (
              <Box
                component="iframe"
                src={pdfBlobUrl}
                ref={pdfViewerRef}
                sx={{
                  width: '100%',
                  height: '800px',
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  backgroundColor: isDarkMode ? alpha(theme.palette.background.paper, 0.5) : '#f5f5f5',
                }}
              />
            ) : (
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress size={30} sx={{ color: primaryColor }} />
                <Typography sx={{ ml: 2 }} color="text.secondary">Loading PDF viewer...</Typography>
              </Box>
            )}
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default PDFManipulation;
